"use client";
import { useEffect, useRef, useState } from "react";

export interface UseInactivityPopupOptions {
  delay?: number;
  sessionStorageKey?: string;
}

interface UseInactivityPopupReturn {
  showPopup: boolean;
  hidePopup: () => void;
  hasUserInteractedWithForm: boolean;
}

export const useInactivityPopup = ({
  delay = 10000,
  sessionStorageKey = "inactivity-popup-shown",
}: UseInactivityPopupOptions = {}): UseInactivityPopupReturn => {
  const [showPopup, setShowPopup] = useState(false);
  const [hasUserInteractedWithForm, setHasUserInteractedWithForm] =
    useState(false);
  const timerRef = useRef<number | null>(null);
  const isPopupShownThisSession = useRef(false);

  useEffect(() => {
    const wasShown = sessionStorage.getItem(sessionStorageKey);
    const wasShownPersistent = localStorage.getItem(sessionStorageKey);
    const formSubmitted = sessionStorage.getItem("formSubmitted");
    const formSubmittedPersistent = localStorage.getItem("formSubmitted");

    if (
      wasShown === "true" ||
      wasShownPersistent === "true" ||
      formSubmitted === "true" ||
      formSubmittedPersistent === "true"
    ) {
      isPopupShownThisSession.current = true;
    }
  }, [sessionStorageKey]);

  const startTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }

    if (isPopupShownThisSession.current || hasUserInteractedWithForm) {
      return;
    }

    timerRef.current = window.setTimeout(() => {
      const formSubmitted = sessionStorage.getItem("formSubmitted");
      const formSubmittedPersistent = localStorage.getItem("formSubmitted");
      const modalOpen = sessionStorage.getItem("modal-open");

      if (
        !hasUserInteractedWithForm &&
        !isPopupShownThisSession.current &&
        formSubmitted !== "true" &&
        formSubmittedPersistent !== "true" &&
        modalOpen !== "true"
      ) {
        setShowPopup(true);
        isPopupShownThisSession.current = true;
        sessionStorage.setItem(sessionStorageKey, "true");
        localStorage.setItem(sessionStorageKey, "true");
      }
    }, delay);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleFormInteraction = (event: Event) => {
    const target = event.target as HTMLElement;

    if (typeof target.matches !== "function") {
      return;
    }

    const isFormElement =
      target.matches(
        'input, textarea, select, option, label, button[type="submit"], [role="combobox"], [role="listbox"], [role="option"], [role="checkbox"], [role="radio"], [role="switch"], [contenteditable="true"]',
      ) ||
      target.closest("form") !== null ||
      target.closest("[data-form]") !== null;

    if (isFormElement && !hasUserInteractedWithForm) {
      setHasUserInteractedWithForm(true);
      sessionStorage.setItem("user-interacted-form", "true");
      stopTimer();
    }
  };

  const hidePopup = () => {
    setShowPopup(false);
    stopTimer();
  };

  useEffect(() => {
    const userInteracted = sessionStorage.getItem("user-interacted-form");
    const formSubmitted = sessionStorage.getItem("formSubmitted");
    const formSubmittedPersistent = localStorage.getItem("formSubmitted");

    if (
      userInteracted === "true" ||
      formSubmitted === "true" ||
      formSubmittedPersistent === "true"
    ) {
      setHasUserInteractedWithForm(true);
      return;
    }

    const events = [
      "click",
      "focus",
      "input",
      "change",
      "keydown",
      "mousedown",
      "touchstart",
    ];

    events.forEach((eventType) => {
      document.addEventListener(eventType, handleFormInteraction, true);
    });

    startTimer();

    return () => {
      events.forEach((eventType) => {
        document.removeEventListener(eventType, handleFormInteraction, true);
      });
      stopTimer();
    };
  }, [delay, hasUserInteractedWithForm]);

  useEffect(() => {
    if (!hasUserInteractedWithForm && !isPopupShownThisSession.current) {
      startTimer();
    }
  }, [hasUserInteractedWithForm]);

  useEffect(() => {
    return () => {
      stopTimer();
    };
  }, []);

  return {
    showPopup,
    hidePopup,
    hasUserInteractedWithForm,
  };
};
